import React, {
  Dispatch,
  SetStateAction,
  useRef,
  useState,
  useEffect,
  useCallback,
  ChangeEvent,
} from 'react'
import './CallEnded.css'
import { AudioTranscription } from '@components/layout/Modals'
import { TranscriptData } from '@dataTypes/message.types'
import { formatMinuteTime } from '@utils/time.util'
import { useCallRecordingObjectUrl } from '@hooks/useCallRecording'

type CallEndedProps = {
  aiCallSummary?: string
  audioTranscript?: TranscriptData[]
  audioUrl: string
  chatId: string
  setShowSummary: Dispatch<SetStateAction<Record<string, boolean>>>
  setShowTranscriptModal: Dispatch<SetStateAction<Record<string, boolean>>>
  showSummary: Record<string, boolean>
  showTranscriptModal: Record<string, boolean>
}

const speedSelection = ['1.00x', '1.25x', '1.50x', '1.75x', '2.00x']

const CallEnded: React.FC<CallEndedProps> = ({
  aiCallSummary,
  audioTranscript,
  audioUrl,
  chatId,
  setShowSummary,
  setShowTranscriptModal,
  showSummary,
  showTranscriptModal,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const progressBarRef = useRef<HTMLDivElement | null>(null)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [currentTime, setCurrentTime] = useState<number>(0)
  const [duration, setDuration] = useState<number>(0)
  const [progress, setProgress] = useState<number>(0)
  const [volume, setVolume] = useState<number>(100)
  const [isMuted, setIsMuted] = useState<boolean>(false)
  const [isShowVolume, setIsShowVolume] = useState<boolean>(false)
  const [speedIndex, setSpeedIndex] = useState<number>(0) // Default to 1.0x
  const playbackSrc = useCallRecordingObjectUrl(audioUrl)

  const updateShowModal = (
    id: string,
    modal: 'transcript' | 'summary',
  ): void => {
    switch (modal) {
      case 'summary':
        const idSummary = !showSummary[id]
        setShowSummary((prev) => ({
          ...prev,
          [id]: idSummary,
        }))
        break
      case 'transcript':
        const idTranscript = !showTranscriptModal[id]
        setShowTranscriptModal((prev) => ({
          ...prev,
          [id]: idTranscript,
        }))
        break
    }
  }

  const handlePlayButton = (): void => {
    const _isPlaying = !isPlaying
    if (audioRef.current) {
      if (_isPlaying) {
        void audioRef.current.play().then(
          () => setIsPlaying(true),
          () => setIsPlaying(false),
        )
      } else {
        audioRef.current.pause()
        setIsPlaying(false)
      }
    }
  }

  const handleRewind = (): void => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(
        0,
        audioRef.current.currentTime - 10,
      )
    }
  }

  const handleForward = (): void => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(
        audioRef.current.duration,
        audioRef.current.currentTime + 10,
      )
    }
  }

  const handleVolumeButton = (): void => {
    const _isMuted = !isMuted
    if (audioRef.current) {
      setIsMuted(_isMuted)
      audioRef.current.muted = _isMuted
    }
  }

  const handleVolumeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newVolume = Number(e.target.value)
    setVolume(newVolume)
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100
      if (newVolume === 0) {
        setIsMuted(true)
        audioRef.current.muted = true
      } else if (isMuted) {
        setIsMuted(false)
        audioRef.current.muted = false
      }
    }
  }

  const handleSpeedChange = (): void => {
    const previousSpeedIndex = speedIndex
    if (previousSpeedIndex === speedSelection.length - 1) {
      setSpeedIndex(0)
    } else {
      setSpeedIndex(previousSpeedIndex + 1)
    }
  }

  const handleProgressClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ): void => {
    if (audioRef.current && progressBarRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect()
      const clickX = e.clientX - rect.left
      const clickPositionRatio = clickX / rect.width
      const newTime = clickPositionRatio * audioRef.current.duration
      audioRef.current.currentTime = newTime
    }
  }

  const updateProgress = useCallback((): void => {
    const audio = audioRef.current
    if (audio && audio.duration > 0) {
      setCurrentTime(audio.currentTime)
      setProgress((audio.currentTime / audio.duration) * 100)
      if (audio.currentTime === audio.duration) {
        setIsPlaying(false)
      }
    }
  }, [])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateDuration = (): void => {
      if (audio.duration) {
        setDuration(audio.duration)
      }
    }

    const handleLoadedMetadata = (): void => {
      updateDuration()
    }

    const handleTimeUpdate = (): void => {
      updateProgress()
    }

    const handleEnded = (): void => {
      setIsPlaying(false)
      setCurrentTime(0)
      setProgress(0)
    }

    // Set playback rate based on speedIndex
    const speedValue = parseFloat(speedSelection[speedIndex].replace('x', ''))
    audio.playbackRate = speedValue

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [speedIndex, updateProgress])

  useEffect(() => {
    const audio = audioRef.current
    if (audio) {
      audio.volume = volume / 100
    }
  }, [volume])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !playbackSrc) return
    audio.load()
    setIsPlaying(false)
    setCurrentTime(0)
    setProgress(0)
    setDuration(0)
  }, [playbackSrc])

  return (
    <div className="call-ended-wrapper">
      <div className="call-ended-container">
        <div className="cec-row-1">
          <i className="mgc_phone_success_fill call-ended-icon"></i>
          <span className="cec-header">Call Ended</span>
          <span className="cec-sub-header">{formatMinuteTime(duration)}</span>
        </div>
        <div className="audio-player-container">
          <audio
            ref={audioRef}
            controls={false}
            className="audio-player"
            src={playbackSrc || undefined}
          />
          <button
            type="button"
            className="unstyled-button time-control-btn rewind-btn mgc_rewind_backward_10_line"
            onClick={handleRewind}
            aria-label="Rewind 10 seconds"
          />
          <button
            type="button"
            className={`unstyled-button play-btn ${isPlaying ? 'mgc_pause_fill' : 'mgc_play_fill'}`}
            onClick={handlePlayButton}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          />
          <button
            type="button"
            className="unstyled-button time-control-btn forward-btn mgc_rewind_forward_10_line"
            onClick={handleForward}
            aria-label="Forward 10 seconds"
          />
          <div className="progress-time-container">
            <span className="current-time">
              {formatMinuteTime(currentTime)}/
            </span>
            <span className="total-duration">{formatMinuteTime(duration)}</span>
            <div
              className="progress-bar-container"
              ref={progressBarRef}
              onClick={handleProgressClick}
            >
              <div className="progress-bar-track">
                <div
                  className="progress-bar-fill"
                  style={{
                    width:
                      progress > 0
                        ? `calc(${progress}% - ${(progress / 100) * 4}px)`
                        : '0px',
                  }}
                >
                  <div
                    className="progress-bar-handle"
                    style={{
                      left: progress === 0 ? '-4px' : `calc(100% - 7px)`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            className="volume-container"
            onMouseEnter={() => setIsShowVolume(true)}
            onMouseLeave={() => setIsShowVolume(false)}
          >
            <button
              type="button"
              className={`unstyled-button volume-btn mgc_volume${isMuted ? '_mute' : ''}_fill`}
              onClick={handleVolumeButton}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            />
            {isShowVolume && (
              <input
                className="volume-slider"
                type="range"
                min="0"
                max="100"
                step="1"
                value={volume}
                onChange={handleVolumeChange}
                onMouseEnter={() => setIsShowVolume(true)}
                onMouseLeave={() => setIsShowVolume(false)}
              />
            )}
          </div>
          <button
            type="button"
            className="unstyled-button speed-btn"
            onClick={handleSpeedChange}
            aria-label="Change playback speed"
          >
            {speedSelection[speedIndex]}
          </button>
        </div>
      </div>
      <div className="call-summary-container">
        <div className="radius-bottom-left" />
        <div className="call-summary-content">
          <div className="csc-row-1">
            <span className="csc-header">AI call cummary</span>
            <button
              type="button"
              className="unstyled-button csc-text-btn"
              onClick={() => updateShowModal(chatId, 'transcript')}
            >
              view transcript
            </button>
          </div>
          <div>
            {aiCallSummary
              ? aiCallSummary
                  .split('\n')
                  .map((line) => line.trim().replace(/^-+\s*/, ''))
                  .filter(Boolean)
                  .map((data, index) => (
                    <span key={index} className="summary-item">
                      <span className="bullet-icon ti ti-circle-filled" />
                      {data}
                    </span>
                  ))
              : 'No AI Summary found'}
          </div>
        </div>
      </div>
      {audioTranscript && (
        <AudioTranscription
          aiSummary={aiCallSummary}
          audioUrl={playbackSrc || audioUrl}
          show={showTranscriptModal[chatId]}
          transcript={audioTranscript}
          toggleModal={setShowTranscriptModal}
        />
      )}
    </div>
  )
}

export default CallEnded
